<script>
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import { useCampaign } from 'shared/composables/useCampaign';
import { dynamicTime } from 'shared/helpers/timeHelper';
import Spinner from 'shared/components/Spinner.vue';
import TableFooterPagination from 'dashboard/components/widgets/TableFooterPagination.vue';
import CampaignsTable from './CampaignsTable.vue';
import EditCampaign from './EditCampaign.vue';
import TemplateOneOffForm from './components/TemplateOneOffForm.vue';
import ViewCampaign from './components/ViewCampaign.vue';
export default {
  components: {
    Spinner,
    TableFooterPagination,
    CampaignsTable,
    EditCampaign,
    TemplateOneOffForm,
    ViewCampaign,
  },
  props: {
    type: {
      type: String,
      default: '',
    },
  },
  setup() {
    const { campaignType, isOneOffType } = useCampaign();
    return { campaignType, isOneOffType };
  },
  data() {
    return {
      showViewPopup: false,
      selectedCampaign: {},
      showDeleteConfirmationPopup: false,
      showRecipientsModal: false,
      selectedRecipientsCampaign: null,
      recipients: [],
      recipientsMeta: {},
      recipientsLoading: false,
    };
  },
  computed: {
    ...mapGetters({
      uiFlags: 'campaigns/getUIFlags',
    }),
    campaigns() {
      return this.$store.getters['campaigns/getCampaigns'](this.campaignType);
    },
    showEmptyResult() {
      const hasEmptyResults =
        !this.uiFlags.isFetching && this.campaigns.length === 0;
      return hasEmptyResults;
    },
  },
  methods: {
    openViewPopup(campaign) {
      this.selectedCampaign = campaign;
      this.showViewPopup = true;
    },
    hideViewPopup() {
      this.showViewPopup = false;
    },
    openDeletePopup(campaign) {
      this.showDeleteConfirmationPopup = true;
      this.selectedCampaign = campaign;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    confirmDeletion() {
      this.closeDeletePopup();
      const { id } = this.selectedCampaign;
      this.deleteCampaign(id);
    },
    async deleteCampaign(id) {
      try {
        await this.$store.dispatch('campaigns/delete', id);
        useAlert(this.$t('CAMPAIGN.DELETE.API.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(this.$t('CAMPAIGN.DELETE.API.ERROR_MESSAGE'));
      }
    },
    async openRecipientsModal(campaign) {
      this.selectedRecipientsCampaign = campaign;
      this.showRecipientsModal = true;
      await this.fetchRecipients(1);
    },
    closeRecipientsModal() {
      this.showRecipientsModal = false;
      this.recipients = [];
      this.recipientsMeta = {};
      this.selectedRecipientsCampaign = null;
    },
    async fetchRecipients(page = 1) {
      if (!this.selectedRecipientsCampaign) return;
      try {
        this.recipientsLoading = true;
        const response = await this.$store.dispatch('campaigns/fetchRecipients', {
          id: this.selectedRecipientsCampaign.id,
          page,
        });
        this.recipients = response.recipients || [];
        this.recipientsMeta = response.meta || {};
      } catch (error) {
        useAlert(this.$t('CAMPAIGN.TEMPLATE.API.ERROR'));
      } finally {
        this.recipientsLoading = false;
      }
    },
    async pauseCampaign(campaign) {
      try {
        await this.$store.dispatch('campaigns/pause', campaign.id);
        useAlert(this.$t('CAMPAIGN.TEMPLATE.API.PAUSE_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('CAMPAIGN.TEMPLATE.API.ERROR'));
      }
    },
    async resumeCampaign(campaign) {
      try {
        await this.$store.dispatch('campaigns/resume', campaign.id);
        useAlert(this.$t('CAMPAIGN.TEMPLATE.API.RESUME_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('CAMPAIGN.TEMPLATE.API.ERROR'));
      }
    },
    async cancelCampaign(campaign) {
      try {
        await this.$store.dispatch('campaigns/cancel', campaign.id);
        useAlert(this.$t('CAMPAIGN.TEMPLATE.API.CANCEL_SUCCESS'));
      } catch (error) {
        useAlert(this.$t('CAMPAIGN.TEMPLATE.API.ERROR'));
      }
    },
    formatRecipientTime(timestamp) {
      if (!timestamp) {
        return '—';
      }
      return dynamicTime(timestamp);
    },
  },
};
</script>

<template>
  <div class="flex-1 overflow-auto">
    <CampaignsTable
      :campaigns="campaigns"
      :show-empty-result="showEmptyResult"
      :is-loading="uiFlags.isFetching"
      :campaign-type="type"
      @view="openViewPopup"
      @delete="openDeletePopup"
      @pause="pauseCampaign"
      @resume="resumeCampaign"
      @cancel="cancelCampaign"
      @viewRecipients="openRecipientsModal"
    />
    <woot-modal :show.sync="showViewPopup" :on-close="hideViewPopup" size="large">
      <ViewCampaign
        :campaign="selectedCampaign"
        @close="hideViewPopup"
      />
    </woot-modal>
    <woot-delete-modal
      :show.sync="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('CAMPAIGN.DELETE.CONFIRM.TITLE')"
      :message="$t('CAMPAIGN.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('CAMPAIGN.DELETE.CONFIRM.YES')"
      :reject-text="$t('CAMPAIGN.DELETE.CONFIRM.NO')"
    />
    <woot-modal
      :show.sync="showRecipientsModal"
      :on-close="closeRecipientsModal"
      size="large"
    >
      <div class="p-4">
        <h3 class="mb-4 text-lg font-semibold text-slate-900 dark:text-slate-100">
          {{ $t('CAMPAIGN.TEMPLATE.RECIPIENTS.TITLE') }}
        </h3>
        <div v-if="recipientsLoading" class="flex items-center gap-2">
          <Spinner color-scheme="primary" />
          <span>{{ $t('CAMPAIGN.TEMPLATE.RECIPIENTS.LOADING') }}</span>
        </div>
        <div v-else>
          <table class="w-full text-sm border border-slate-200 dark:border-slate-700 rounded">
            <thead class="bg-slate-50 dark:bg-slate-800 text-left">
              <tr>
                <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
                  {{ $t('CAMPAIGN.TEMPLATE.RECIPIENTS.PHONE') }}
                </th>
                <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
                  {{ $t('CAMPAIGN.TEMPLATE.RECIPIENTS.STATE') }}
                </th>
                <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
                  {{ $t('CAMPAIGN.TEMPLATE.RECIPIENTS.ATTEMPTS') }}
                </th>
                <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
                  {{ $t('CAMPAIGN.TEMPLATE.RECIPIENTS.UPDATED') }}
                </th>
                <th class="px-4 py-2 text-slate-600 dark:text-slate-300">
                  {{ $t('CAMPAIGN.TEMPLATE.RECIPIENTS.ERROR') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="recipient in recipients"
                :key="recipient.id"
                class="border-t border-slate-200 dark:border-slate-700"
              >
                <td class="px-4 py-2 text-slate-700 dark:text-slate-200">
                  {{ recipient.phone }}
                </td>
                <td class="px-4 py-2 text-slate-600 dark:text-slate-300 capitalize">
                  {{ recipient.state }}
                </td>
                <td class="px-4 py-2 text-slate-600 dark:text-slate-300">
                  {{ recipient.attempts }}
                </td>
                <td class="px-4 py-2 text-slate-600 dark:text-slate-300">
                  {{ formatRecipientTime(recipient.updated_at) }}
                </td>
                <td class="px-4 py-2 text-slate-600 dark:text-slate-300">
                  {{ recipient.error_message || '—' }}
                </td>
              </tr>
              <tr v-if="!recipients.length">
                <td
                  colspan="5"
                  class="px-4 py-6 text-center text-slate-600 dark:text-slate-300"
                >
                  {{ $t('CAMPAIGN.TEMPLATE.RECIPIENTS.EMPTY') }}
                </td>
              </tr>
            </tbody>
          </table>
          <div class="flex justify-end mt-4">
            <TableFooterPagination
              :current-page="recipientsMeta.current_page || 1"
              :total-pages="recipientsMeta.total_pages || 1"
              @pageChange="fetchRecipients"
            />
          </div>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<style scoped lang="scss">
.button-wrapper {
  @apply flex justify-end pb-2.5;
}
</style>
